import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, MessageCircle, Clock } from "lucide-react";
import UpiPayCard from "../UpiPayCard";
import { BRAND, errMsg, inr } from "./constants";

function LeadRow({ ld, open }) {
  return (
    <div data-testid={`lead-row-${ld.id}`} className="py-2 border-t flex items-center justify-between gap-2 text-sm" style={{ borderColor: BRAND.line }}>
      <div className="min-w-0">
        <div className={`font-bold ${open ? "" : "blur-[3px] select-none"}`} data-testid={`lead-name-${ld.id}`}>{ld.name}</div>
        <div className="text-xs" style={{ color: BRAND.muted }}><span className={open ? "" : "blur-[3px] select-none"} data-testid={`lead-phone-${ld.id}`}>{ld.phone}</span>{ld.city ? ` · ${ld.city}` : ""}{ld.travel_date ? ` · ${ld.travel_date}` : ""}</div>
      </div>
      {open && <a href={ld.wa_url} target="_blank" rel="noreferrer" data-testid={`lead-wa-${ld.id}`} className="chip text-xs inline-flex items-center gap-1" style={{ color: "#16A34A" }}><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
    </div>
  );
}

function BatchCard({ b, onPaid }) {
  const open = b.status === "unlocked";
  return (
    <div data-testid={`lead-batch-${b.id}`} className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: open ? BRAND.green : BRAND.line }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.green }}>
          {open ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" style={{ color: BRAND.orange }} />} Batch #{b.batch_no} · {b.leads.length} travellers
        </div>
        <span data-testid={`lead-batch-status-${b.id}`} className="text-[10px] font-bold uppercase" style={{ color: open ? BRAND.green : BRAND.orange }}>
          {open ? (b.price_inr ? "unlocked" : "free · unlocked") : b.payment_pending ? "payment under review" : `locked · ${inr(b.price_inr)}`}
        </span>
      </div>
      <div className="mt-2">{b.leads.map((ld) => <LeadRow key={ld.id} ld={ld} open={open} />)}</div>
      {!open && (b.payment_pending
        ? <div data-testid={`lead-batch-pending-${b.id}`} className="mt-3 text-xs inline-flex items-center gap-1.5" style={{ color: BRAND.orange }}><Clock className="w-3.5 h-3.5" /> Payment logged — contacts unlock once admin confirms.</div>
        : <div className="mt-3"><UpiPayCard amount={b.price_inr} purpose="homestay_leads" referenceId={b.id} onLogged={onPaid} /></div>)}
    </div>
  );
}

export function LeadsPanel({ pid, authFetch, API }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/lead-batches` }).then((r) => setItems(r.data.items))
    .catch((e) => toast.error(errMsg(e, "Could not load leads"))), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="leads-panel" className="space-y-4">
      <div>
        <div className="text-lg font-bold" style={{ color: BRAND.green }}>Contact leads</div>
        <p className="text-xs" style={{ color: BRAND.muted }}>HeylpMate sends you batches of 10 travellers looking for a stay. Contacts stay hidden until the batch is unlocked.</p>
      </div>
      {items && items.length === 0 && <div data-testid="leads-empty" className="rounded-2xl border-2 border-dashed p-6 text-sm text-center" style={{ borderColor: BRAND.line, color: BRAND.muted }}>No lead batches yet. HeylpMate admin will drop new travellers here.</div>}
      <div className="grid lg:grid-cols-2 gap-4">{(items || []).map((b) => <BatchCard key={b.id} b={b} onPaid={load} />)}</div>
    </div>
  );
}
